/* eslint-disable @typescript-eslint/no-use-before-define */
import moment from 'moment';
import { DateRange } from 'moment-range';
import PropTypes from 'prop-types';

/**
 * Custom React component prop type validators.
 * More info: https://www.npmjs.com/package/prop-types
 */
const ssPropTypes = {};
export default ssPropTypes;

/**
 * Number or string number
 */
ssPropTypes.numberLike = createTypeChecker((props, propName) => {
  const value = props[propName];
  // isNaN is different than Number.isNaN, so disable rule for this line
  // eslint-disable-next-line no-restricted-globals
  if (value === null || isNaN(value)) {
    return new Error(`${propName} is not a number.`);
  }
  return null;
});

/**
 * Validates if a property is a momentjs Date object.
 */
ssPropTypes.moment = createTypeChecker((props, propName) => {
  const value = props[propName];
  // need to check both the imported moment and the window moment, because they might be different
  if (value && !moment.isMoment(value) && !window.moment.isMoment(value)) {
    return new Error(`${propName} is not a moment date object.`);
  }
  return null;
});

/**
 * Validates if a property is a MomentRange object.
 */
ssPropTypes.momentRange = createTypeChecker((props, propName) => {
  const value = props[propName];
  if (value && !(value instanceof DateRange)) {
    return new Error(`${propName} is not a DateRange object.`);
  }
  return null;
});

/**
 * Is this a valid Credit/Debit card instrument object.
 */
ssPropTypes.creditCard = PropTypes.shape({
  id: PropTypes.number, // card within appointment object does not have id
  bank: PropTypes.string.isRequired,
  last4: ssPropTypes.numberLike,
});

/**
 * A single search result item
 */
ssPropTypes.searchResult = PropTypes.shape({
  version: PropTypes.string,
  salon_id: ssPropTypes.numberLike.isRequired,
  salon_name: PropTypes.string,
  num_ratings: PropTypes.number.isRequired,
  average_rating: PropTypes.number.isRequired,
  isNCDEligible: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    latitude: PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired,
    distance: PropTypes.number,
  }),
  provider_id: ssPropTypes.numberLike.isRequired,
  provider_name: PropTypes.string.isRequired,
  services: PropTypes.arrayOf(PropTypes.shape({
    id: ssPropTypes.numberLike.isRequired,
  })),
  popularCategories: PropTypes.arrayOf(PropTypes.shape({
    category: PropTypes.string.isRequired,
    popularity: PropTypes.number.isRequired,
  })),
});

/**
 * A provider gallery image
 */
ssPropTypes.galleryImage = PropTypes.shape({
  id: PropTypes.number,
  provider: PropTypes.number,
  client: PropTypes.number,
  order: PropTypes.number,
  appointment_id: PropTypes.number,
  service_id: PropTypes.number,
  path_root: PropTypes.string,
  url: PropTypes.url,
  data: PropTypes.any, // a raw File instance
  cover_image: PropTypes.bool,
  variant: PropTypes.string,
  supportsAdvancedFields: PropTypes.bool,
  supportsCaption: PropTypes.bool,
  rotate: PropTypes.number,
  crop_left: PropTypes.number,
  crop_right: PropTypes.number,
  crop_top: PropTypes.number,
  crop_bottom: PropTypes.number,
  type: PropTypes.number,
});

/**
 * Create a prop-types type checker function.
 * More info: https://www.ian-thomas.net/custom-proptype-validation-with-react/
 *
 * @param {Function} validate - The function to validate the prop type
 * @return {Function} Chainable prop-types validation function.
 */
function createTypeChecker(validate) {
  function checkType(isRequired, props, propName, componentName, location) {
    if (props[propName] == null) {
      if (isRequired) {
        return new Error(`Prop '${propName}' was not specified.`);
      }
      return null;
    }
    return validate(props, propName, componentName, location);
  }

  const chainedCheckType = checkType.bind(null, false);
  chainedCheckType.isRequired = checkType.bind(null, true);
  return chainedCheckType;
}
